import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Modal, Form, Input, Button, Select, Checkbox, Row, Col } from 'antd'; // Removed 'notification' import
import api from '../../utils/api';
import { USER_TYPE_VALUES, USER_TYPE_LABELS } from '../../const/userTypes'; 
import { useNotificationApi } from '../../context/NotificationContext'; // <--- NEW IMPORT

const { Option } = Select;

const UserForm = ({ visible, onCancel, onSuccess, user }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [internalOrgId, setInternalOrgId] = useState(null);
  const [internalOrgName, setInternalOrgName] = useState('');
  const [supervisors, setSupervisors] = useState([]); 
  
  const [currentUserType, setCurrentUserType] = useState(''); 
  const [dataReady, setDataReady] = useState(false);

  const notificationApi = useNotificationApi(); // <--- NEW: Get notification API from context

  // REMOVED: const [apiNotification, contextHolderNotification] = notification.useNotification({ maxCount: 3 });

  // Helper function to show errors (persistent notification, top-right)
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key); // <--- Use notificationApi from context
    try {
      notificationApi.error({ // <--- Use notificationApi from context
        key,
        message: title,
        description: content,
        duration: 4.5, 
        placement: 'topRight',
        style: {
          width: 400, 
          zIndex: 10000, // Ensure high z-index
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({ 
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]); // Depend on notificationApi from context

  // Helper function to show success (transient notification, top-right)
  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({ // <--- Use notificationApi from context
      key,
      message: title,
      description: description,
      duration: 3, 
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000, // Ensure high z-index
      },
    });
  }, [notificationApi]); // Depend on notificationApi from context

  // Helper function to show loading (sticky notification, top-right, with delay and min display time)
  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300; 
    const minDisplayTime = 500; 

    const startTime = Date.now(); 
    
    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({ // <--- Use notificationApi from context
        key,
        message: messageContent,
        duration: 0, 
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000, // Ensure high z-index
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer); 
      const elapsed = Date.now() - startTime; 
      const remaining = Math.max(0, minDisplayTime - elapsed);
      
      setTimeout(() => {
        if (dismissNotification) { 
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key); // <--- Use notificationApi from context
        } else {
          notificationApi.destroy(key); // <--- Use notificationApi from context
        }
      }, remaining);
    };
  }, [notificationApi]); // Depend on notificationApi from context

  const roleTypeMappings = useMemo(() => ({
    'internal_staff': [1, 2, 3], 
    'partner': [4, 5, 9],         
    'customer': [6, 7, 8]         
  }), []);

  const handleUserTypeChange = useCallback((value) => {
    setCurrentUserType(value);
    
    form.setFieldsValue({ roleId: undefined, organizationId: undefined });

    const currentRoleId = form.getFieldValue('roleId');
    const isValidCurrentRole = currentRoleId !== undefined && roleTypeMappings[value]?.includes(currentRoleId);

    if (value === 'internal_staff') {
      if (internalOrgId !== null) {
        form.setFieldsValue({ organizationId: internalOrgId });
      } else {
        showError('Internal Organization Missing', 'No internal organization with type "internal" found. Please ensure it exists in your database and is active.', 'internalOrgWarning');
        form.setFieldsValue({ organizationId: undefined });
      }
      if (!user || !isValidCurrentRole) {
          const defaultInternalRole = roleTypeMappings['internal_staff'].find(id => id === 3); 
          if (defaultInternalRole) {
              form.setFieldsValue({ roleId: defaultInternalRole });
          } else {
              showError('Default Role Missing', 'Default internal staff role not found in mappings.', 'defaultRoleWarning');
          }
      }
    } else if (value === 'partner') {
        if (!user || !isValidCurrentRole) {
            const defaultPartnerRole = roleTypeMappings['partner'].find(id => id === 5); 
            if (defaultPartnerRole) {
                form.setFieldsValue({ roleId: defaultPartnerRole });
            }
        }
    } else if (value === 'customer') {
        if (!user || !isValidCurrentRole) {
            const defaultCustomerRole = roleTypeMappings['customer'].find(id => id === 7); 
            if (defaultCustomerRole) {
                form.setFieldsValue({ roleId: defaultCustomerRole });
            }
        }
    }
  }, [form, internalOrgId, user, roleTypeMappings, showError]); 


  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setCurrentUserType('');
      setOrganizations([]);
      setRoles([]);
      setInternalOrgId(null);
      setInternalOrgName('');
      setSupervisors([]); 
      setDataReady(false);
      return;
    }

    const fetchAllData = async () => {
      setLoading(true);
      setDataReady(false);
      const loadingKey = 'userFormLoading';
      const hideLoading = showLoading(loadingKey, 'Loading form data...');

      try {
        const [rolesResponse, orgsResponse, usersResponse] = await Promise.all([ 
          api.get('/api/users/roles'),
          api.get('/api/organizations'),
          api.get('/api/users/active-internal-and-partner') 
        ]);
        
        let fetchedRolesData = rolesResponse.data.data || rolesResponse.data; 
        if (!Array.isArray(fetchedRolesData)) {
          console.error('UserForm: API /api/users/roles did not return an array or expected object format:', rolesResponse.data);
          fetchedRolesData = [];
        }
        setRoles(fetchedRolesData);

        let fetchedOrgsData = orgsResponse.data.data || orgsResponse.data; 
        if (!Array.isArray(fetchedOrgsData)) {
          console.error('UserForm: API /api/organizations did not return an array or expected object format:', orgsResponse.data);
          fetchedOrgsData = [];
        }
        setOrganizations(fetchedOrgsData);

        let fetchedSupervisorsData = usersResponse.data?.data || []; 
        if (!Array.isArray(fetchedSupervisorsData)) {
          console.error('UserForm: API /api/users/active-internal-and-partner did not return an array or expected object format:', usersResponse.data);
          fetchedSupervisorsData = [];
        }
        const filteredSupervisors = fetchedSupervisorsData.filter(u => u.isActive && (!user || u.id !== user.id)); 
        setSupervisors(filteredSupervisors);

        const internalOrganization = fetchedOrgsData.find(org => org.type === 'internal');
        if (internalOrganization) {
          setInternalOrgId(internalOrganization.id);
          setInternalOrgName(internalOrganization.name);
        } else {
          console.warn('UserForm: No organization with type "internal" found. Please ensure it exists in your database and is active.');
          setInternalOrgId(null);
          setInternalOrgName('');
        }
        
        setDataReady(true);
        hideLoading();
      } catch (error) {
        console.error('UserForm: Error fetching form data:', error.response?.data || error.message, error);
        hideLoading();
        showError('Failed to Load Form Data', error.response?.data?.message || 'An unexpected error occurred while loading user form data.', 'userFormLoadError');
        setRoles([]); 
        setOrganizations([]);
        setInternalOrgId(null);
        setInternalOrgName('');
        setSupervisors([]); 
        setDataReady(false);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [visible, form, user, showLoading, showError]); 

  useEffect(() => {
    if (!dataReady || !visible) {
      return; 
    }

    if (user) {
      const initialValues = {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        designation: user.designation, 
        phoneNumber: user.phoneNumber,
        userType: user.userType,
        roleId: typeof user.roleId === 'number' ? user.roleId : parseInt(user.roleId, 10),
        isActive: Boolean(user.isActive),
        supervisorId: user.supervisorId || undefined, 
        organizationId: user.organizationId || undefined
      };
      form.setFieldsValue(initialValues);
      setCurrentUserType(user.userType || ''); 
    } else {
      form.resetFields();
      form.setFieldsValue({ isActive: true });
      handleUserTypeChange('internal_staff'); 
    }
  }, [dataReady, visible, user, form, handleUserTypeChange]);


  const handleSubmit = async () => {
    let hideLoading; 
    try {
      setLoading(true);
      const loadingKey = 'userSubmitLoading';
      hideLoading = showLoading(loadingKey, user ? 'Updating user...' : 'Creating user...'); 

      const values = await form.validateFields(); 
      
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        designation: values.designation, 
        phoneNumber: values.phoneNumber || null,
        userType: values.userType,
        roleId: values.roleId,
        isActive: values.isActive,
        supervisorId: values.supervisorId || null, 
      };

      if (values.userType === 'internal_staff') {
        payload.organizationId = internalOrgId; 
      } else {
        payload.organizationId = values.organizationId || null; 
      }

      if (user) {
        await api.put(`/api/users/${user.id}`, payload); 
        showSuccess('User Updated', 'User details updated successfully!', 'userUpdateSuccess');

        if (values.newPassword) {
          await api.put(`/api/users/${user.id}/change-password`, { newPassword: values.newPassword }); 
          showSuccess('Password Updated', 'User password updated successfully!', 'passwordUpdateSuccess');
        }

      } else {
        payload.password = values.password; 
        await api.post('/api/users', payload); 
        showSuccess('User Created', 'New user created successfully!', 'userCreateSuccess');
      }

      setTimeout(() => {
        if (hideLoading && typeof hideLoading === 'function') hideLoading();
        onSuccess(true); 
        onCancel(); 
      }, 500); 

    } catch (error) {
      if (hideLoading && typeof hideLoading === 'function') hideLoading(); 
      console.error('UserForm: Form submission error:', error.response?.data || error.message, error);
      const errorMessage = error.response?.data?.message || 'Failed to save user.';
      showError('Submission Failed', errorMessage, 'userFormSubmitError');
    } finally {
      setLoading(false);
    }
  };

  const filteredRoles = roles.filter(role => 
    roleTypeMappings[currentUserType]?.includes(role.id)
  );

  return (  
    <Modal
      open={visible} 
      title={user ? 'Edit User' : 'Add New User'}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Submit
        </Button>,
      ]}
      width={700}
    >
      {/* REMOVED: {contextHolderNotification} */} 
      <Form
        form={form} 
        layout="vertical"
        name="user_form"
        initialValues={!user ? { isActive: true } : {}} 
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[{ required: true, message: 'Please input first name' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[{ required: true, message: 'Please input last name' }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true, message: 'Please input email' }, { type: 'email', message: 'Please enter a valid email' }]}
            >
              <Input 
                readOnly={!!user} 
                style={user ? { backgroundColor: '#f5f5f5', color: '#8c8c8c', cursor: 'not-allowed' } : {}} 
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              name="designation"
              label="Designation"
              rules={[{ required: true, message: 'Please input designation' }]} 
            >
              <Input placeholder="e.g., System Engineer" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="phoneNumber" 
              label="Phone Number"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="userType"
              label="User Type"
              rules={[{ required: true, message: 'Please select user type' }]}
            >
              <Select 
                placeholder="Select type" 
                onChange={handleUserTypeChange} 
                disabled={!!user}
              >
                <Option value="internal_staff">Internal Staff</Option>
                <Option value="partner">Partner</Option>
                <Option value="customer">Customer</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="roleId"
              label="Role"
              rules={[{ required: true, message: 'Please select role' }]}
            >
              <Select 
                showSearch 
                placeholder="Select a role"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
                loading={loading}
              >
                {filteredRoles.length > 0 ? (
                  filteredRoles.map(role => (
                    <Option key={role.id} value={role.id}> 
                      {role.name} 
                    </Option>
                  ))
                ) : (
                  <Option value="no-data" disabled>No roles available for this user type</Option>
                )}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            {currentUserType === 'internal_staff' ? (
              <Form.Item
                label="Organization Name"
              >
                <Input 
                  value={internalOrgName || user?.organizationName || 'N/A'} 
                  readOnly 
                  disabled
                  style={{ backgroundColor: '#f5f5f5', color: '#8c8c8c', cursor: 'not-allowed' }} 
                />
                <Form.Item name="organizationId" noStyle rules={[{ required: true, message: 'Internal organization is required.' }]}>
                    <Input type="hidden" />
                </Form.Item>
              </Form.Item>
            ) : (
              <Form.Item
                name="organizationId"
                label="Organization"
                rules={[{ required: true, message: 'Please select an organization' }]}
              >
                <Select 
                  placeholder="Select organization" 
                  loading={loading} 
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {organizations
                    .filter(org => org.type === currentUserType)
                    .map(org => (
                      <Option key={org.id} value={org.id}>
                        {org.name || `ID: ${org.id}`}
                      </Option>
                    ))}
                  {organizations.length === 0 && (
                    <Option value="no-data" disabled>No organizations found</Option>
                  )}
                </Select>
              </Form.Item>
            )}
          </Col>
        </Row>
        
        <Row gutter={16}>
          <Col span={24}> 
            <Form.Item
              name="supervisorId" 
              label="Supervisor"
              rules={[{ required: false, message: 'Please select a supervisor' }]} 
            >
              <Select
                showSearch
                allowClear 
                placeholder="Select a supervisor (optional)" 
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
                loading={loading} 
              >
                {supervisors.length > 0 ? (
                  supervisors.map(sup => (
                    <Option key={sup.id} value={sup.id}>
                      {`${sup.firstName || ''} ${sup.lastName || ''} | ${sup.email || ''}`}
                    </Option>
                  ))
                ) : (
                  <Option value="no-data" disabled>No active users available to be supervisors</Option>
                )}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {!user && (
          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Please input password' }]}
          >
            <Input.Password />
          </Form.Item>
        )}

        {user && (
          <Form.Item
            name="newPassword" 
            label="New Password (optional)"
            rules={[
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password placeholder="Leave blank to keep current password" />
          </Form.Item>
        )}

        <Form.Item
          name="isActive"
          label="Status"
          valuePropName="checked"
        >
          <Checkbox>Active</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UserForm;